/**
 * SearchResults — displays search results from the searchPages API.
 * Shows page title, library name, and a text snippet with highlighted match.
 * Click navigates to the page.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as pagesApi from '../../api/pages-api';
import type { PageSearchResult } from '../../api/pages-api';
import { usePagesContext } from '../../state/pages-context';

interface SearchResultsProps {
  readonly query: string;
  readonly onClose?: () => void;
}

export function SearchResults({ query, onClose }: SearchResultsProps): React.ReactElement {
  const navigate = useNavigate();
  const { state } = usePagesContext();
  const [results, setResults] = useState<PageSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch search results when query changes
  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      return;
    }

    let cancelled = false;

    async function search(): Promise<void> {
      setLoading(true);
      setError(null);
      try {
        const response = await pagesApi.searchPages(query, {
          libraryId: state.selectedLibraryId ?? undefined,
        });
        if (!cancelled) {
          setResults(response.data ?? []);
        }
      } catch (err: unknown) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Search failed');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    search();
    return () => { cancelled = true; };
  }, [query, state.selectedLibraryId]);

  const handleResultClick = useCallback((result: PageSearchResult) => {
    navigate(`/pages/${result.libraryId}/${result.pageId}`);
    onClose?.();
  }, [navigate, onClose]);

  // Find library name by ID
  const getLibraryName = (libraryId: string): string => {
    const lib = state.libraries.find((l) => l.libraryId === libraryId);
    return lib?.name ?? 'Unknown library';
  };

  // Highlight matching text in snippet
  const highlightMatch = (text: string, searchTerm: string): React.ReactNode => {
    if (!searchTerm.trim()) return text;

    const parts = text.split(new RegExp(`(${escapeRegex(searchTerm)})`, 'gi'));
    return parts.map((part, index) =>
      part.toLowerCase() === searchTerm.toLowerCase() ? (
        <mark key={index} className="bg-yellow-200 text-yellow-900 rounded px-0.5">
          {part}
        </mark>
      ) : (
        <span key={index}>{part}</span>
      )
    );
  };

  if (!query.trim()) {
    return <></>;
  }

  if (loading) {
    return (
      <div className="p-3" role="status" aria-live="polite">
        <p className="text-sm text-muted-foreground">Searching...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-3" role="alert">
        <p className="text-sm text-destructive">{error}</p>
      </div>
    );
  }

  if (results.length === 0) {
    return (
      <div className="p-3">
        <p className="text-sm text-muted-foreground">
          No results found for &ldquo;{query}&rdquo;.
        </p>
      </div>
    );
  }

  return (
    <section aria-label="Search results" className="flex flex-col">
      <p className="px-3 py-2 text-xs text-muted-foreground border-b">
        {results.length} result{results.length !== 1 ? 's' : ''} for &ldquo;{query}&rdquo;
      </p>
      <ul className="divide-y" role="list" aria-label="Search results list">
        {results.map((result) => (
          <li key={result.pageId}>
            <button
              onClick={() => handleResultClick(result)}
              className="w-full text-left px-3 py-2.5 hover:bg-accent/50 transition-colors"
            >
              <p className="text-sm font-medium truncate">
                {highlightMatch(result.title, query)}
              </p>
              <p className="text-xs text-muted-foreground truncate mt-0.5">
                {getLibraryName(result.libraryId)}
              </p>
              {result.snippet && (
                <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                  {highlightMatch(result.snippet, query)}
                </p>
              )}
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Utility
// ---------------------------------------------------------------------------

function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
